import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Options, SeriesOptionsType } from 'highcharts';
import { WidgetEntryWithData, WidgetWithData } from '@models/widget/widget.model';
import { SPANISH_SHORT_MONTHS } from '../highcharts-lang-es.constant';

@Component({
  standalone: false,
  selector: 'app-widget-line-chart',
  templateUrl: './widget-line-chart.component.html',
  styleUrls: ['./widget-line-chart.component.scss'],
})
export class WidgetLineChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) widget!: WidgetWithData;
  @Input() isPreview = false;
  @Input() showChrome = false;
  @Input() typeLabel = '';
  @Input() sourceLabel = '';
  @Input() isEditable = false;
  @Input() canDelete = true;
  @Input() isEditMode = false;
  @Input() isMockPreview = false;
  @Input() sensorTypeLabels: string[] = [];

  @Output() edit   = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Options = {};
  updateFlag = false;
  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chart = chart;
    this.scheduleChartReflow();
  };
  private readonly defaultDecimals = 1;
  private chart?: Highcharts.Chart;
  private resizeObserver?: ResizeObserver;
  private reflowHandle: number | null = null;
  private reflowTimeoutHandle: number | null = null;
  private cap(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {
    Highcharts.setOptions({
      time: { useUTC: true },
      lang: { shortMonths: SPANISH_SHORT_MONTHS },
    } as any);
  }

  ngOnChanges(): void {
    this.updateFlag = false;
    this.chartOptions = this.buildChartOptions();
    this.updateFlag = true;
    this.scheduleChartReflow();
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const target = this.elementRef.nativeElement.querySelector('.dashboard-widget-card__content')
        ?? this.elementRef.nativeElement;
      this.resizeObserver = new ResizeObserver(() => this.scheduleChartReflow());
      this.resizeObserver.observe(target);
      this.scheduleChartReflow();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.reflowHandle !== null) {
      window.cancelAnimationFrame(this.reflowHandle);
    }
    if (this.reflowTimeoutHandle !== null) {
      window.clearTimeout(this.reflowTimeoutHandle);
    }
  }

  private scheduleChartReflow(): void {
    if (this.reflowHandle !== null) {
      window.cancelAnimationFrame(this.reflowHandle);
    }
    if (this.reflowTimeoutHandle !== null) {
      window.clearTimeout(this.reflowTimeoutHandle);
    }

    const runReflow = () => {
      this.reflowHandle = window.requestAnimationFrame(() => {
        this.reflowHandle = null;
        this.chart?.reflow();
      });
    };

    if (!this.isEditMode) {
      runReflow();
      return;
    }

    this.reflowTimeoutHandle = window.setTimeout(() => {
      this.reflowTimeoutHandle = null;
      runReflow();
    }, 90);
  }

  private buildChartOptions(): Options {
    const decimals = this.defaultDecimals;
    const series: SeriesOptionsType[] = this.widget.entries.map((serie) => {
      const data          = this.toChartData(serie);
      const hasSinglePoint = data.length === 1;
      const isBar          = serie.visualizationType === 'BAR';

      if (isBar) {
        return {
          type:        'column',
          name:        this.cap(serie.label || serie.fieldName || 'Serie'),
          color:       serie.color || '#3B82F6',
          data,
          borderWidth: 0,
          borderRadius: 4,
          custom: { unit: serie.unit ?? '' },
        } as SeriesOptionsType;
      }

      return {
        type:      'line',
        name:      serie.label || serie.fieldName || 'Serie',
        color:     serie.color || '#3B82F6',
        data:      hasSinglePoint
          ? data.map(d => ({ x: d[0], y: d[1], marker: { enabled: true, radius: 4 } }))
          : data,
        marker:    { enabled: false, radius: 2.5, symbol: 'circle' },
        lineWidth: 2.2,
        custom:    { unit: serie.unit ?? '' },
      } as SeriesOptionsType;
    });

    return {
      accessibility: { enabled: false },
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        spacing: [8, 8, 4, 6],
        animation: !this.isPreview && !this.isMockPreview && !this.isEditMode,
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: !this.isPreview && series.length > 0,
        align: 'left',
        verticalAlign: 'top',
        itemDistance: 12,
        symbolWidth: 8,
        symbolHeight: 8,
        itemMarginTop: 0,
        itemMarginBottom: 0,
        itemStyle: { color: '#64748b', fontSize: '10px', fontWeight: '500' },
      },
      xAxis: {
        type: 'datetime',
        labels: { enabled: !this.isPreview, style: { color: '#64748b', fontSize: '10px' } },
        lineColor: '#cbd5e1',
        tickColor: '#cbd5e1',
        tickLength: 4,
        dateTimeLabelFormats: {
          millisecond: '%H:%M',
          second:      '%H:%M',
          minute:      '%H:%M',
          hour:        '%H:%M',
          day:         '%b %e',
          week:        '%b %e',
          month:       '%b %Y',
          year:        '%Y',
        },
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: { color: '#a0aab8', fontSize: '10px' },
          format: '{value}',
        },
        gridLineColor: '#f4f7fb',
        gridLineDashStyle: 'Dash',
      },
      tooltip: {
        enabled: !this.isPreview,
        shared: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter(this: any): string {
          const pts = this.points ?? [];
          const dateStr = Highcharts.dateFormat('%d/%m/%Y %H:%M', this.x as number);
          const rows = pts.map(p => {
            const unit = (p.series.userOptions.custom as Record<string, string>)?.['unit'] ?? '';
            const val  = p.y != null ? (p.y as number).toFixed(2).replace(/\.?0+$/, '') : '--';
            return `<span style="color:${String(p.color)}">●</span> ${p.series.name}: <b>${val}${unit ? ' ' + unit : ''}</b>`;
          });
          return `<small style="color:#6b7280">${dateStr}</small><br>${rows.join('<br>')}`;
        },
      },
      exporting: { enabled: false },
      plotOptions: {
        series: {
          animation: !this.isPreview && !this.isMockPreview && !this.isEditMode,
          states: { inactive: { opacity: 1 }, hover: { lineWidthPlus: 0 } },
        },
      },
      series,
    };
  }

  get hasNoData(): boolean {
    return (this.widget.entries ?? []).every(s => !s.data?.length);
  }

  get timeRangeLabel(): string {
    const map: Record<string, string> = {
      '1h':  'Última hora',
      '6h':  'Últimas 6 horas',
      '24h': 'Últimas 24 horas',
      '7d':  'Últimos 7 días',
      '30d': 'Últimos 30 días',
    };
    const tr = this.widget.config?.timeRange;
    return tr ? (map[tr] ?? tr) : '';
  }

  private toChartData(serie: WidgetEntryWithData): Array<[number, number]> {
    return (serie.data ?? []).map((point) => {
      const d = new Date(point.date);
      const ts = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
      const raw = Number(point.value);
      const val = Number.isInteger(raw) ? raw : Math.round((raw + Number.EPSILON) * 100) / 100;
      return [ts, val];
    });
  }
}
